import { FlaskConical } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useMatch, useParams } from 'react-router';

import { MixedList, PatternBanner } from '@components/patterns';
import { Badge, Link, Text, Tooltip } from '@components/primitives';

import { useAppQuery } from '@queries/app';
import { docsHref } from '@utils/docs';

import css from './styles.module.css';

const SandboxIcon = FlaskConical;

function SandboxIndicator() {
  return (
    <Badge>
      <SandboxIcon />
      Sandbox
    </Badge>
  );
}

function SandboxTooltip() {
  return (
    <div className={css.tooltip}>
      <Text size="sm" leading="md" color="var(--grey-60)">
        Sandbox apps let you test Evervault enterprise features without
        affecting production data.{' '}
        <Link href={docsHref('/developers/sandbox')}>Learn more</Link>
      </Text>
    </div>
  );
}

function SandboxBanner() {
  const { teamUuid, appUuid } = useParams();
  const { data: app } = useAppQuery(teamUuid ?? '', appUuid ?? '', {
    enabled: !!teamUuid && !!appUuid,
  });

  const isAdmin = useMatch('/admin/*');

  const isSandbox = !!app?.sandbox;

  return (
    <AnimatePresence initial={false}>
      {!isAdmin && isSandbox && (
        <PatternBanner
          className={css.banner}
          trackHeightWith="--sandbox-height"
        >
          <Tooltip
            content={<SandboxTooltip />}
            side="bottom"
            sideOffset={14}
            interactive
          >
            <span className={css.label}>
              <SandboxIcon aria-hidden />
              <span>Sandbox app</span>
            </span>
          </Tooltip>
        </PatternBanner>
      )}
    </AnimatePresence>
  );
}

interface SandboxInfoProps {
  isSandbox: boolean;
}

function SandboxInfo({ isSandbox }: SandboxInfoProps) {
  return (
    <MixedList.Row>
      <MixedList.Data>
        <h6>App mode</h6>
        <p>{isSandbox ? 'Sandbox' : 'Production'}</p>
      </MixedList.Data>
    </MixedList.Row>
  );
}

export const Sandbox = {
  Icon: SandboxIcon,
  Indicator: SandboxIndicator,
  Banner: SandboxBanner,
  Info: SandboxInfo,
  Tooltip: SandboxTooltip,
};
